import { faSoapNote } from "@awesome.me/kit-0572d5a008/icons/kit/custom";
import { DSCardWrapper } from "@components/design-system/molecules";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarEmpty } from "@fortawesome/pro-regular-svg-icons";
import {
  faArrowRight,
  faShareNodes,
  faStar,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRecordingJobType } from "@hooks/use-recording-job-type";
import mixpanel from "mixpanel-browser";
import { useState } from "react";
import { useNavigate } from "react-router";

import { DSModal } from "../design-system/organisms";

export default function TemplateSaveSuccessModal({
  isOpen,
  onClose,
  onShareTemplate,
  templateUuid,
  templateName,
  templateType,
  creationMethod,
  showFeedback = false,
}) {
  const [feedbackRating, setFeedbackRating] = useState(null);
  const [hoverRating, setHoverRating] = useState(0);
  const { setSelectedJobType } = useRecordingJobType();
  const navigate = useNavigate();

  const handleFeedbackRating = (rating) => {
    setFeedbackRating(rating);
    mixpanel.track("Template Creation Feedback", {
      templateUuid,
      rating,
      templateName,
      templateType,
      creationMethod,
    });
  };

  const handleClose = () => {
    setFeedbackRating(null);
    setHoverRating(0);
    onClose();
  };

  return (
    <DSModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Your Template is Ready!"
      subtitle="Your template has been saved. What would you like to do next?"
      dataTestId="template-success-modal"
      actions={[
        {
          label: "Close",
          variant: "cancel",
          onClick: handleClose,
        },
      ]}
    >
      <div className="flex flex-col gap-md">
        <button
          type="button"
          onClick={() => {
            setSelectedJobType({
              value: templateUuid,
              name: templateName,
              icon: templateType === "SOAP" ? faSoapNote : faFile,
            });
            navigate("/dashboard");
          }}
          className="flex items-center gap-md p-md rounded-large border border-secondary bg-secondary hover:border-brand transition-colors text-left"
          data-testid="template-success-use-template"
        >
          <FontAwesomeIcon
            icon={faArrowRight}
            className="text-brand text-xl shrink-0"
          />
          <div className="flex-1">
            <h5 className="text-base font-semibold text-primary">
              Use this Template
            </h5>
            <p className="text-sm text-secondary">
              Go to the dashboard and start a recording with your new
              template.
            </p>
          </div>
        </button>

        <button
          type="button"
          onClick={onShareTemplate}
          className="flex items-center gap-md p-md rounded-large border border-secondary bg-secondary hover:border-brand transition-colors text-left"
          data-testid="template-success-share-template"
        >
          <FontAwesomeIcon
            icon={faShareNodes}
            className="text-brand text-xl shrink-0"
          />
          <div className="flex-1">
            <h5 className="text-base font-semibold text-primary">
              Share this Template
            </h5>
            <p className="text-sm text-secondary">
              Share with your team or generate a public link for
              others.
            </p>
          </div>
        </button>

        {showFeedback && (
          <DSCardWrapper
            variant="ghost"
            data-testid="template-feedback-section"
          >
            <div className="flex flex-col items-center">
              {feedbackRating ? (
                <p className="text-sm text-secondary">
                  Thanks for your feedback!
                </p>
              ) : (
                <>
                  <p className="text-sm font-medium text-primary">
                    How easy was template creation?
                  </p>
                  <div
                    className="flex items-center gap-sm"
                    onMouseLeave={() => setHoverRating(0)}
                  >
                    <span className="text-xs text-secondary">
                      Difficult
                    </span>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleFeedbackRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        className="text-2xl transition-colors cursor-pointer"
                        data-testid={`template-feedback-star-${star}`}
                        aria-label={`Rate ${star} out of 5`}
                      >
                        <FontAwesomeIcon
                          icon={
                            star <=
                            (hoverRating || feedbackRating || 0)
                              ? faStar
                              : faStarEmpty
                          }
                          className={
                            star <=
                            (hoverRating || feedbackRating || 0)
                              ? "text-yellow-400"
                              : "text-secondary"
                          }
                        />
                      </button>
                    ))}
                    <span className="text-xs text-secondary">
                      Super easy
                    </span>
                  </div>
                </>
              )}
            </div>
          </DSCardWrapper>
        )}
      </div>
    </DSModal>
  );
}
